import { z } from 'zod';

/**
 * CRM Integration data connectors: a UI-configured JSON API whose records are
 * mapped into stock_listings (DealerKit data shape) so the stock search can read them.
 */

/** Mapping targets: top-level columns plus dot paths inside stock_listings.data that the
 *  stock search reads. Extra fields use `custom.<name>` (the existing custom-fields bucket). */
export const STOCK_TARGET_FIELDS = [
  'external_id',
  'registration',
  'vin',
  'status',
  'vehicle.manufacturer',
  'vehicle.model',
  'vehicle.derivative',
  'vehicle.trim',
  'vehicle.year',
  'vehicle.mileage',
  'vehicle.colour',
  'vehicle.fuel_type',
  'vehicle.body_type',
  'vehicle.transmission_type',
  'vehicle.engine_size',
  'vehicle.bhp',
  'vehicle.drive_train',
  'vehicle.condition',
  'vehicle.number_of_doors',
  'vehicle.number_of_seats',
  'owners',
  'prices.advertised.amount',
  'prices.monthly.amount',
  'media.images',
  'location.name',
  'location.telephone',
  'location.postcode',
  'location.latitude',
  'location.longitude',
  'links.website',
  'notes',
] as const;

/**
 * The fixed values our stock uses for these fields (DealerKit's own spelling, taken from
 * staging stock 2026-10-01). Connector value maps translate source values into these.
 */
export const STOCK_MASTER_VALUES = {
  'vehicle.fuel_type': [
    'Petrol',
    'Diesel',
    'Electric',
    'Petrol Hybrid',
    'Petrol Plug-in Hybrid',
    'Diesel Hybrid',
    'Diesel Plug-in Hybrid',
  ],
  'vehicle.body_type': [
    'SUV',
    'Hatchback',
    'Estate',
    'Saloon',
    'Coupe',
    'Convertible',
    'MPV',
    'Pickup',
    'Panel Van',
    'Combi Van',
  ],
  'vehicle.transmission_type': ['Automatic', 'Manual'],
  'vehicle.drive_train': ['FWD', '4WD', 'RWD'],
} as const satisfies Record<string, readonly string[]>;
export type MasterValueField = keyof typeof STOCK_MASTER_VALUES;

export const CUSTOM_TARGET_PATTERN = /^custom\.[a-z][a-z0-9_]{0,49}$/;

const targetSchema = z
  .string()
  .refine(
    (t) => (STOCK_TARGET_FIELDS as readonly string[]).includes(t) || CUSTOM_TARGET_PATTERN.test(t),
    'Target must be a known stock field or custom.<snake_case_name>',
  );

export const mappingTransformSchema = z.enum([
  'text',
  'number',
  'integer',
  'boolean',
  'date',
  'first',
  'list',
  'image_list',
]);
export type MappingTransform = z.infer<typeof mappingTransformSchema>;

/** A quantified group that itself contains a quantifier — the classic catastrophic-backtracking
 *  shape ((a+)+, (a*)*b, (x+){2,}). Value rules run on every synced record. */
const NESTED_QUANTIFIER_RE = /\([^()]*[+*][^()]*\)\s*(?:[+*]|\{\d+,?\d*\})/;

const isValidRegex = (pattern: string): boolean => {
  try {
    new RegExp(pattern, 'i');
    return true;
  } catch {
    return false;
  }
};

/** After the value map: if `path` in the record matches `pattern` (and the value is
 *  `when_value`, when given), the value becomes `set`. E.g. hybrid → plug-in from the model. */
export const valueRuleSchema = z.object({
  path: z.string().min(1).max(200),
  pattern: z
    .string()
    .min(1)
    .max(200)
    .refine(isValidRegex, 'pattern must be a valid regex')
    .refine(
      (p) => !NESTED_QUANTIFIER_RE.test(p),
      'pattern must not repeat a group that already repeats, e.g. (a+)+ (it can freeze the server)',
    ),
  when_value: z.string().optional(),
  set: z.string(),
});
export type ValueRule = z.infer<typeof valueRuleSchema>;

export const fieldMappingSchema = z.object({
  target: targetSchema,
  path: z.string().min(1).max(200),
  transform: mappingTransformSchema.default('text'),
  /** Source value → our value (matched ignoring case/spaces); '' stores empty. */
  value_map: z.record(z.string(), z.string()).optional(),
  value_rules: z.array(valueRuleSchema).max(20).optional(),
});
export type FieldMapping = z.infer<typeof fieldMappingSchema>;

export const splitLevelSchema = z.object({
  /** Query param set to the bucket value, e.g. "make" then "model". */
  param: z.string().min(1),
  /** Extra params for the count request, e.g. { rows: "0", facets: "make|0|1000" }. */
  facet_params: z.record(z.string(), z.string()),
  /** JSON path to the bucket array in the facet response, e.g. "facets.make". */
  buckets_path: z.string().min(1),
  value_key: z.string().min(1).default('item'),
  count_key: z.string().min(1).default('count'),
});
export type SplitLevel = z.infer<typeof splitLevelSchema>;

export const connectorPaginationSchema = z.object({
  mode: z.enum(['offset', 'page']),
  /** offset mode: start param; page mode: page-number param. */
  param: z.string().min(1),
  size_param: z.string().min(1),
  page_size: z.number().int().min(1).max(5000),
  /** page mode only: number of the first page (0 or 1). */
  first_page: z.number().int().min(0).max(1).default(1),
  /** Provider's deepest reachable offset (start + rows), e.g. 15000. */
  max_depth: z.number().int().min(1).max(10_000_000),
  delay_ms: z.number().int().min(0).max(60_000).default(250),
});

export const dataConnectorConfigSchema = z.object({
  request: z.object({
    url: z.string().url(),
    method: z.enum(['GET', 'POST']).default('GET'),
    headers: z.record(z.string(), z.string()).default({}),
    /** Values may contain {{secret.<name>}}. */
    params: z.record(z.string(), z.string()).default({}),
  }),
  records_path: z.string().min(1),
  total_path: z.string().min(1).optional(),
  pagination: connectorPaginationSchema,
  split: z.array(splitLevelSchema).max(2).default([]),
  mapping: z
    .array(fieldMappingSchema)
    .min(1)
    .refine((m) => m.some((f) => f.target === 'external_id'), 'Map one field to external_id'),
  /**
   * Cars the source no longer lists (missing = 'ask'): 'ask' when a run would mark over 20% unavailable;
   * 'auto' marks them unavailable without asking (still asks over 50%, e.g. a provider outage).
   */
  unlisted_cars: z.enum(['ask', 'auto']).optional(),
});
export type DataConnectorConfig = z.infer<typeof dataConnectorConfigSchema>;
export type DataConnectorConfigInput = z.input<typeof dataConnectorConfigSchema>;

const secretsSchema = z.record(z.string().regex(/^[a-z][a-z0-9_]{0,39}$/), z.string());
const scheduleTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

export const createDataConnectorSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  is_active: z.boolean().default(true),
  config: dataConnectorConfigSchema,
  /** Plain values in; stored encrypted. On update: omitted key = keep, '' = delete. */
  secrets: secretsSchema.default({}),
  schedule_enabled: z.boolean().default(false),
  schedule_interval_days: z.number().int().min(1).max(90).default(7),
  schedule_time: scheduleTimeSchema.default('02:00'),
});
export type CreateDataConnectorDto = z.infer<typeof createDataConnectorSchema>;

/** No defaults here: a PATCH must never reset fields it didn't send. */
export const updateDataConnectorSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(500).nullable().optional(),
  is_active: z.boolean().optional(),
  config: dataConnectorConfigSchema.optional(),
  secrets: secretsSchema.optional(),
  schedule_enabled: z.boolean().optional(),
  schedule_interval_days: z.number().int().min(1).max(90).optional(),
  schedule_time: scheduleTimeSchema.optional(),
});
export type UpdateDataConnectorDto = z.infer<typeof updateDataConnectorSchema>;

export const testDataConnectorSchema = z.object({
  config: dataConnectorConfigSchema,
  secrets: secretsSchema.optional(),
  /** When editing: saved secrets fill any secret not supplied above. */
  connector_id: z.string().uuid().optional(),
});
export type TestDataConnectorDto = z.infer<typeof testDataConnectorSchema>;

/** The admin's answer to "N cars are no longer listed — mark them unavailable?". */
export const retireDecisionSchema = z.object({ approve: z.boolean() });
export type RetireDecisionDto = z.infer<typeof retireDecisionSchema>;

/** field → source value → count */
export type UnmatchedValues = Record<string, Record<string, number>>;

export type DataConnectorStatus = 'idle' | 'running' | 'error';

export interface DataConnectorDetail {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  config: DataConnectorConfig;
  secret_names: string[];
  schedule_enabled: boolean;
  schedule_interval_days: number;
  schedule_time: string;
  status: DataConnectorStatus;
  last_run_at: string | null;
  last_run_status: string | null;
  listing_count: number;
  created_at: string;
  updated_at: string;
}

export interface DataConnectorRun {
  id: string;
  connector_id: string;
  /** needs_approval: would mark >20% of active cars unavailable — waiting for the admin. */
  status: 'running' | 'success' | 'failed' | 'needs_approval';
  trigger: 'manual' | 'scheduled';
  requests: number;
  slices_total: number;
  slices_done: number;
  records_parsed: number;
  records_skipped: number;
  upserted: number;
  marked_unavailable: number;
  truncated_slices: string[];
  /** Values that matched no master value: field → value → count. */
  unmatched_values: UnmatchedValues;
  error: string | null;
  /** Failed on a temporary error: auto-resumed by the scheduler (up to 3 times). */
  retryable: boolean;
  resume_count: number;
  /** needs_approval: how many cars the decision would mark unavailable. */
  pending_retire: number;
  retire_decision: 'approved' | 'kept' | null;
  started_at: string;
  finished_at: string | null;
}

export interface ConnectorTestMappedRow {
  external_id: string | null;
  data: Record<string, unknown>;
  skip?: string;
}

export interface ConnectorTestResult {
  request_url: string;
  status: number;
  total: number | null;
  returned: number;
  short_page_warning: string | null;
  sample_raw: unknown[];
  sample_mapped: ConnectorTestMappedRow[];
  paths: string[];
  /** Values in the sample that matched no master value: field → value → count. */
  unmatched: UnmatchedValues;
}
